import { STORAGE_KEY } from '@/constant/constant';
import {
  CartItem,
  filterExcludedItems,
  findChangedItemId,
  getExcludedProductIds,
} from '@/utils/addCart/core';
import { getSession, removeSession, setSession } from '@/utils/storage';
import { safeJsonParse } from '@/utils/safeJsonParse';

export interface CartSnapshot {
  items: CartItem[];
  rawItems: any[];
  blocked?: boolean;
}

export interface AddCartAdapter {
  name: string;
  fetchSnapshot: () => Promise<CartSnapshot>;
  detectEvent: (onPossibleAdd: () => void) => void;
  getExcludedProductIds?: () => string[];
  isCartPage?: () => boolean;
  /**
   * 触发后轮询无变化时的兜底：
   * 'always' = 仍用首项弹窗（shoplazza 的 master 语义）；
   * 'whenNoOld' = 仅无存量快照时才弹首项（shopline 的 master 语义，默认）
   */
  noChangeFallback?: 'always' | 'whenNoOld';
  mapLoadPayload: (rawItem: any) => any;
}

interface CreateAddCartTrackerOptions {
  adapter: AddCartAdapter;
  load: any;
  retryMax?: number;
  retryDelayMs?: number;
  initialStoreDelayMs?: number;
  triggerStoreDelayMs?: number;
}

export const createAddCartTracker = (options: CreateAddCartTrackerOptions) => {
  const {
    adapter,
    load,
    retryMax = 10,
    retryDelayMs = 500,
    initialStoreDelayMs = 0,
    triggerStoreDelayMs = 2000,
  } = options;

  const isCartPage = () =>
    adapter.isCartPage
      ? adapter.isCartPage()
      : window?.location?.pathname?.indexOf('/cart') !== -1;
  const setAddCartItems = (items: CartItem[], delayMs: number) => {
    if (delayMs > 0) {
      setTimeout(
        () => setSession(STORAGE_KEY.ADD_CART_ITEMS, JSON.stringify(items)),
        delayMs,
      );
    } else {
      setSession(STORAGE_KEY.ADD_CART_ITEMS, JSON.stringify(items));
    }
  };

  const getFilteredSnapshot = async () => {
    const snapshot = await adapter.fetchSnapshot();
    const excludedProductIds = adapter.getExcludedProductIds
      ? adapter.getExcludedProductIds()
      : getExcludedProductIds();
    const filteredItems = filterExcludedItems(
      snapshot.items,
      excludedProductIds,
    );
    // rawItems 保持未过滤：master 的兜底弹窗（line_items[0]）和判空都基于原始列表
    return {
      ...snapshot,
      items: filteredItems,
    };
  };

  const loadByChangedId = (
    changedId: string | number | null,
    rawItems: any[],
  ) => {
    if (!changedId) return;
    const rawItem = rawItems.find(
      (item) => String(item?.product_id ?? item?.id) === String(changedId),
    );
    if (rawItem && load) {
      load(2, 'form/out/addPurchaseLoad', {
        name: adapter.name,
        res: adapter.mapLoadPayload(rawItem),
      });
    }
  };

  const syncOnInit = async () => {
    const snapshot = await getFilteredSnapshot();
    if (snapshot.blocked) return;
    const oldItems = safeJsonParse<CartItem[]>(
      getSession(STORAGE_KEY.ADD_CART_ITEMS) ?? '',
      [],
    );
    const changedId = findChangedItemId(snapshot.items, oldItems);

    setAddCartItems(snapshot.items, initialStoreDelayMs);
    // 与 master 一致：hasChange && state==='1' 时无条件清状态，购物车页判断仅控制是否 load
    if (changedId) {
      if (getSession(STORAGE_KEY.ADD_CART_STATE) === '1') {
        removeSession(STORAGE_KEY.ADD_CART_STATE);
        if (isCartPage()) {
          loadByChangedId(changedId, snapshot.rawItems);
        }
      }
    } else {
      removeSession(STORAGE_KEY.ADD_CART_STATE);
    }
  };

  const pollAfterTrigger = async (time = 0): Promise<void> => {
    const snapshot = await getFilteredSnapshot();
    // 与 master 一致：响应结构异常（blocked）或购物车为空时按空车重试，不清状态
    if (snapshot.blocked || !snapshot.rawItems.length) {
      if (time < retryMax) {
        setTimeout(() => {
          pollAfterTrigger(time + 1);
        }, retryDelayMs);
      }
      return;
    }

    const oldItems = safeJsonParse<CartItem[]>(
      getSession(STORAGE_KEY.ADD_CART_ITEMS) ?? '',
      [],
    );
    const hasOld = !!getSession(STORAGE_KEY.ADD_CART_ITEMS);
    const changedId = findChangedItemId(snapshot.items, oldItems);

    if (changedId) {
      setAddCartItems(snapshot.items, triggerStoreDelayMs);
      loadByChangedId(changedId, snapshot.rawItems);
      return;
    }

    const noChangeFallback = adapter.noChangeFallback ?? 'whenNoOld';
    if (noChangeFallback === 'always' || !hasOld) {
      const firstItem = snapshot.rawItems[0];
      if (firstItem && load) {
        load(2, 'form/out/addPurchaseLoad', {
          name: adapter.name,
          res: adapter.mapLoadPayload(firstItem),
        });
      }
      return;
    }
    removeSession(STORAGE_KEY.ADD_CART_STATE);
  };

  const onPossibleAdd = () => {
    if (getSession(STORAGE_KEY.ADD_CART_STATE) === '1') return;
    setSession(STORAGE_KEY.ADD_CART_STATE, '1');
    pollAfterTrigger(0);
  };

  return {
    start: () => {
      if (getSession(STORAGE_KEY.ADD_CART_STATE) === '1' && !isCartPage()) {
        removeSession(STORAGE_KEY.ADD_CART_STATE);
      }
      syncOnInit().catch(() => {});
      adapter.detectEvent(onPossibleAdd);
    },
  };
};
